import React from 'react';
import { AlertTriangle, AlertCircle, Info, CheckCircle, LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

export type AlertType = 'warning' | 'error' | 'info' | 'success';
export type AlertSize = 'xs' | 'sm' | 'md';

interface AlertProps {
  message: string;
  type: AlertType;
  size?: AlertSize;
  className?: string;
}

const alertConfig: Record<AlertType, {
  icon: LucideIcon;
  bgClass: string;
  borderClass: string;
  textClass: string;
  iconClass: string;
}> = {
  warning: {
    icon: AlertTriangle,
    bgClass: 'bg-yellow-50',
    borderClass: 'border-yellow-200',
    textClass: 'text-yellow-800',
    iconClass: 'text-yellow-600',
  },
  error: {
    icon: AlertCircle,
    bgClass: 'bg-red-50',
    borderClass: 'border-red-200',
    textClass: 'text-red-800',
    iconClass: 'text-red-600',
  },
  info: {
    icon: Info,
    bgClass: 'bg-blue-50',
    borderClass: 'border-blue-200',
    textClass: 'text-blue-800',
    iconClass: 'text-blue-600',
  },
  success: {
    icon: CheckCircle,
    bgClass: 'bg-green-50',
    borderClass: 'border-green-200',
    textClass: 'text-green-800',
    iconClass: 'text-green-600',
  },
};

/**
 * Displays a reusable alert message with different types and sizes.
 *
 * @param {string} message - The message content to display in the alert.
 * @param {AlertType} type - The type of alert ('warning', 'error', 'info', 'success').
 * @param {AlertSize} [size='md'] - The size of the alert ('xs', 'sm' or 'md').
 * @param {string} [className] - Optional additional CSS classes for the container.
 * @returns {React.ReactElement | null} The rendered alert component or null.
 */
export const Alert: React.FC<AlertProps> = ({ 
  message, 
  type, 
  size = 'md',
  className = '' 
}) => {
  const { t } = useTranslation();
  const config = alertConfig[type];

  if (!message) return null;

  const IconComponent = config.icon;
  const translatedMessage = t(message, { defaultValue: message });

  let paddingClass = 'p-4';
  let iconSizeClass = 'w-5 h-5';
  let gapClass = 'gap-3';

  if (size === 'sm') {
    paddingClass = 'p-2';
    iconSizeClass = 'w-4 h-4';
    gapClass = 'gap-2';
  } else if (size === 'xs') {
    paddingClass = 'px-2 py-1';
    iconSizeClass = 'w-4 h-4';
    gapClass = 'gap-1.5';
  }

  return (
    <div 
      className={`rounded-lg border flex items-start ${paddingClass} ${gapClass} ${config.bgClass} ${config.borderClass} ${className}`}
      role="alert"
    >
      <div className={`flex-shrink-0 mt-0.5 ${config.iconClass}`}>
        <IconComponent className={iconSizeClass} aria-hidden="true" />
      </div>
      <div className={`${config.textClass}`}>
        {translatedMessage}
      </div>
    </div>
  );
}; 